<template>
  <div class="user-nav">
    <nav v-if="loggedIn && user" class="user-menu copy-regular" :aria-label="t('userMenu')">
      <ul class="list" role="menu">
        <li class="item user-head" tabindex="0">
          <img :src="getMemberImageUrl(user.profilePicture)" alt="" class="profile-picture _hidden-mobile" />
          <span class="username _hidden-mobile">{{ user.name }}</span>
          <SvgArrowDown class="icon-arrow-down _hidden-mobile" aria-hidden="true" />
          <ul class="dropdown">
            <li class="item edit-profile">
              <NuxtLink :to="profilePageUrl">{{ t('editProfile') }}</NuxtLink>
            </li>
            <li class="item logout">
              <button type="button" class="button button-secondary-vs" @click="logout">
                <SvgLinkBold class="icon-link _hidden-desktop" aria-hidden="true" />
                {{ t('logout') }}
              </button>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
    <NuxtLink v-else type="button" class="loggedout button-secondary" :to="loginPageUrl">
      <SvgUser class="user-icon" aria-hidden="true" /> {{ t('login') }}
    </NuxtLink>
  </div>
</template>

<i18n lang="yaml">
de:
  userMenu: Benutzermenü
  editProfile: Profil bearbeiten
  logout: Logout
  login: Anmelden
en:
  userMenu: User Menu
  editProfile: Edit Profile
  logout: Logout
  login: Login
</i18n>

<script setup lang="ts">
import SvgArrowDown from '../content-modules/svgs/SvgArrowDown.vue';
import SvgLinkBold from '../content-modules/svgs/SvgLinkBold.vue';
import SvgUser from '../content-modules/svgs/SvgUser.vue';
import { useAlumniSettings } from '~/app/useAlumniSettings';
import { getMemberImageUrl } from '~/alumni/easyVerein.utils';

const { t } = useI18n();

const { loggedIn, user, clear: clearSession } = useUserSession();
const { loginPageUrl, profilePageUrl } = useAlumniSettings();

async function logout() {
  // Make a full page reload so that the navigation reloads properly without any unnecessary reactiveness magic...
  await clearSession();
  window.location.href = loginPageUrl;
}
</script>

<style scoped lang="scss">
.user-nav {
  > .user-menu {
    border-radius: 1.5rem;
    background: var(--color-main-blue-light);
    position: relative;

    &::after {
      @include pseudo();

      inset: 0;
      border-radius: 1.5rem;
      background: var(--color-main-blue-light);
      z-index: -1;
    }

    @media (--md), (--lg) {
      padding: 0.5rem 0.75rem;

      @media (hover: hover) {
        &:hover {
          &::after {
            border-radius: 1.5rem 1.5rem 0 0;
          }

          > .list > .user-head > .dropdown {
            display: grid;
          }
        }
      }
    }

    > .list > .user-head {
      display: flex;
      align-items: center;

      @media (--xs), (--sm) {
        flex-direction: column;
      }

      @media (--md), (--lg) {
        gap: var(--spacing-4xs);
      }

      &:focus-visible {
        outline-color: var(--color-sky);
      }

      > .profile-picture {
        aspect-ratio: 1;
        border-radius: 50%;

        @media (--xs), (--sm) {
          margin-bottom: var(--spacing-4xs);
          width: 4.5rem;
        }

        @media (--md), (--lg) {
          width: 1.5rem;
        }
      }

      > .username {
        cursor: default;

        @media (--xs), (--sm) {
          margin-bottom: var(--spacing-3xs);
        }
      }

      > .dropdown {
        @media (--xs), (--sm) {
          display: grid;
          gap: var(--spacing-3xs);
          width: 100%;
        }

        @media (--md), (--lg) {
          display: none;
          position: absolute;
          top: calc(100% - 1px); // -1px to avoid subpixel rendering issue
          left: 0;
          width: 100%;
          padding: 0.5rem 0.75rem 1rem;
          background: var(--color-main-blue-light);
          border-radius: 0 0 1.5rem 1.5rem;
          gap: var(--spacing-4xs);
        }

        > .item {
          &.edit-profile {
            @media (--xs), (--sm) {
              @include pill-alumni;
            }
          }

          &.logout {
            > .button {
              @media (--xs), (--sm) {
                display: flex;
                align-items: center;
                gap: var(--spacing-4xs);

                > :deep(.icon-link) {
                  fill: var(--color-white);
                  width: 1.25rem;
                  height: 1.25rem;
                }
              }
            }
          }

          > * {
            display: block;
            width: 100%;
            cursor: pointer;

            @media (--xs), (--sm) {
              text-align: center;
            }

            @media (--md), (--lg) {
              text-align: left;
            }

            &.-link-exact-active {
              @media (--md), (--lg) {
                font-weight: bold;
              }
            }

            &:focus-visible {
              border: 2px solid var(--color-sky);
              border-radius: 4px;
              transition: none;
            }
          }
        }
      }
    }
  }

  > .loggedout {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        :deep(.user-icon .fill-color) {
          fill: var(--color-night);
        }
      }
    }
  }
}
</style>
